import {
    getCoralStoryId,
    inlineArticleAds,
    metrics,
    TheWestSection,
} from '@news-mono/component-library'
import {
    AdTargeting,
    BaseClientConfig,
    ContentDataTypes,
    GetRouteAdTargeting,
    PageType,
    RenderTarget,
} from '@news-mono/web-common'
import {
    ArticleV4DTO,
    PageMetaDTO,
} from '@west-australian-newspapers/publication-types'
import { layout } from '../../../App.routing'
import { regionalSiteConfig } from '../../regionals/regional-site-config'
import { getAmpArticleComposition } from './article.amp'

// Used for Quicklinks
export const publicationPageType = 'publication'

export const createArticleRoute = ({
    article,
    config,
    section,
    getAdTargeting,
    pageMeta,
    renderTarget,
}: {
    article: ArticleV4DTO
    config: BaseClientConfig
    section: TheWestSection
    getAdTargeting: GetRouteAdTargeting
    renderTarget: RenderTarget
    pageMeta?: PageMetaDTO
}): PageType<TheWestSection> => {
    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'article',
        section,
        article.topics.primary,
        ...article.topics.secondary,
    )

    const adTargeting: AdTargeting = {
        pageId: article.slug,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    // Used to keep track of the promo card options setup on the west desk
    const westPromotionCardTopics = [
        'news',
        'sport',
        'business',
        'politics',
        'lifestyle',
        'entertainment',
        'travel',
        'technology',
    ]

    const primaryTopicId = article.topics.primary.parent
        ? article.topics.primary.parent.id
        : article.topics.primary.id

    const promoCardMetadataKey = `${
        westPromotionCardTopics.includes(primaryTopicId)
            ? primaryTopicId
            : 'news'
    }-promotional-card`

    return {
        kind: 'page',
        heading: article.heading,
        pageMeta: {
            ...pageMeta,
            description: article.homepageTeaser,
            title: article.seoHead || undefined,
        },
        socialMeta: {
            title: article.socialHead || article.homepageHead,
            description: article.socialTeaser || article.homepageTeaser,
        },
        hideHeading: true,
        noMetaTitleTemplate: false,
        pageType: publicationPageType,
        adTargeting,
        additionalPageProperties: {
            primaryTopic: article.topics.primary,
            secondaryTopics: article.topics.secondary,
            publicationKind: article.kind,
            // DPT-1375 extra Metadata for variant testing. Remove when finished
            requiredAccess: article.requiredAccess,
            layout: article.layoutView,
        },
        section,
        compositions:
            renderTarget === 'amp'
                ? [getAmpArticleComposition(article, section)]
                : [
                      layout.composition({
                          type: 'box',
                          props: {
                              containerWidth:
                                  metrics.thewest.siteMetrics.mainContentWidth,
                              horizontalGutters: 'outerMargin',
                              verticalGutters: ['outerMargin', 'unset'],
                          },
                          contentAreas: {
                              main: [
                                  layout.component({
                                      type: 'ad-unit',
                                      feature: 'outstream',
                                      props: {
                                          noticePosition: 'below-center',
                                          hiddenUntilLoaded: false,
                                          padding: [0],
                                          slot: {
                                              id: 'outstream2x2',
                                              size: 'outstream',
                                          },
                                      },
                                  }),
                                  layout.component({
                                      type: 'ad-unit',
                                      feature: 'ad-tech-dfp-testing',
                                      props: {
                                          noticePosition: 'below-center',
                                          hiddenUntilLoaded: false,
                                          padding: [0],
                                          slot: {
                                              id: '2x1_test_pixel',
                                              size: '2x1_test_pixel',
                                          },
                                      },
                                  }),
                                  ...getBreakingNewsStrap(section),
                              ],
                          },
                      }),
                      layout.composition({
                          type: 'athena',
                          props: {
                              containerWidth:
                                  metrics.thewest.siteMetrics.mainContentWidth,
                              sidebarOptions: 'hiddenMobile',
                              elementType: 'article',
                          },
                          contentAreas: {
                              main: [
                                  layout.component({
                                      type: 'topic-list',
                                      props: {
                                          topics: article.topics,
                                          verticalSpacing: 'sm',
                                      },
                                  }),

                                  layout.component({
                                      type: 'article',
                                      props: {
                                          meta: {
                                              slug: article.slug,
                                              kind: 'article',
                                              inlinePublicationContent: [
                                                  inlineArticleAds({
                                                      insertAfter: 3,
                                                      noticePosition:
                                                          'below-center',
                                                      hiddenUntilLoaded: false,
                                                      mastHead: 'thewest',
                                                      slotID: 'article-mrec-one',
                                                      size: 'below768Mrec',
                                                      pageType: 'article',
                                                  }),
                                                  inlineArticleAds({
                                                      insertAfter: 12,
                                                      noticePosition:
                                                          'below-center',
                                                      hiddenUntilLoaded: true,
                                                      mastHead: 'thewest',
                                                      slotID: 'article-mrec-two',
                                                      size: 'below768Mrec',
                                                      pageType: 'article',
                                                  }),
                                                  inlineArticleAds({
                                                      insertAfter: 21,
                                                      noticePosition:
                                                          'below-center',
                                                      hiddenUntilLoaded: true,
                                                      mastHead: 'thewest',
                                                      slotID: 'article-mrec-three',
                                                      size: 'below768Mrec',
                                                      pageType: 'article',
                                                  }),
                                                  inlineArticleAds({
                                                      insertAfter: 30,
                                                      noticePosition:
                                                          'below-center',
                                                      hiddenUntilLoaded: false,
                                                      mastHead: 'thewest',
                                                      slotID: 'article-mrec-four',
                                                      size: 'below768Mrec',
                                                      pageType: 'article',
                                                  }),
                                                  {
                                                      kind: 'the-west-live-in-article-banner',
                                                      insertAfter: 6,
                                                  },
                                                  {
                                                      kind: 'sport-call-to-action',
                                                      insertAfter: 6,
                                                  },
                                                  {
                                                      kind: 'sports-hub-call-to-action',
                                                      insertAfter: 3,
                                                  },
                                                  {
                                                      kind: 'oovvuu-featured-card',
                                                      insertAfter: 7,
                                                      props: {
                                                          oovvuuUrl:
                                                              config.oovvuuUrl,
                                                          oovvuuPath:
                                                              config.oovvuuPath,
                                                      },
                                                  },
                                                  {
                                                      kind: 'inline-pollie-rater-banner',
                                                      insertAfter: 8,
                                                  },
                                              ],
                                          },
                                          dataDefinitionArgs: {
                                              publicationId: article.id,
                                          },
                                          section,
                                          isVideoStickyEnabled: true,
                                      },
                                  }),

                                  layout.component({
                                      type: 'content-separator',
                                      props: {
                                          verticalBottomSpacing: 'lg',
                                      },
                                  }),

                                  layout.nestedComposition({
                                      type: 'box',
                                      props: {
                                          verticalSpacing: 'lg',
                                      },
                                      contentAreas: {
                                          main: [
                                              layout.component({
                                                  type: 'coral',
                                                  feature: 'comments',
                                                  props: {
                                                      storyID: getCoralStoryId(
                                                          section,
                                                          config.publicHostname,
                                                          article.id,
                                                      ),
                                                      articleCommentsEnabled:
                                                          article.allowCommenting,
                                                  },
                                              }),
                                          ],
                                      },
                                  }),

                                  layout.nestedComposition({
                                      type: 'janus',
                                      props: {
                                          verticalSpacing: 'sm',
                                          stretchColumns: true,
                                          layoutRatio: '2:3',
                                          breakpoints: {
                                              layoutRatiosBreakpoint: 'xs',
                                          },
                                      },
                                      contentAreas: {
                                          left: [
                                              layout.component({
                                                  type: 'curated-promotional-card',
                                                  props: {
                                                      fontScale: 1,
                                                      verticalSpacing: 'lg',
                                                      fixedRatios: {
                                                          default: ['4:3'],
                                                          md: '16:9',
                                                      },
                                                      hasBackground: true,
                                                      hasPadding: true,
                                                      mediaMode: 'edgeToEdge',
                                                      showCTA: true,

                                                      dataDefinitionArgs: {
                                                          type: 'active-promotion',
                                                          promotionSlotId:
                                                              primaryTopicId,
                                                          metadataKey:
                                                              promoCardMetadataKey,
                                                      },
                                                  },
                                              }),
                                          ],
                                          right: [
                                              layout.component({
                                                  type: 'publication-related-content',
                                                  props: {
                                                      dataDefinitionArgs: {
                                                          publicationId:
                                                              article.id,
                                                      },
                                                  },
                                              }),
                                          ],
                                      },
                                  }),

                                  layout.component({
                                      type: 'taboola',
                                      feature:
                                          section !== 'default'
                                              ? 'taboola-regionals'
                                              : 'taboola',
                                      props: {
                                          taboolaContainerId:
                                              'taboola-below-article-thumbnails',
                                          locationType: 'article',
                                      },
                                  }),
                                  layout.nestedComposition({
                                      type: 'janus',
                                      feature: 'ripple',
                                      props: {
                                          stretchColumns: true,
                                          layoutRatio: '1:1',
                                      },
                                      contentAreas: {
                                          left: [
                                              layout.component({
                                                  type: 'ripple',
                                                  props: {
                                                      pageSection:
                                                          getRippleSectionTargeting(
                                                              section,
                                                              article,
                                                          ),
                                                  },
                                              }),
                                          ],

                                          right: [
                                              layout.component({
                                                  type: 'ripple',
                                                  props: {
                                                      pageSection:
                                                          getRippleSectionTargeting(
                                                              section,
                                                              article,
                                                          ),
                                                  },
                                              }),
                                          ],
                                      },
                                  }),
                                  ...articleFollowers(section),
                              ],
                              sidebar: [
                                  layout.component({
                                      type: 'ad-unit',
                                      props: {
                                          noticePosition: 'below-center',
                                          padding: [
                                              0,
                                              0,
                                              metrics.thewest.margins.md,
                                              0,
                                          ],
                                          slot: {
                                              id: 'sidebar-top',
                                              size: 'desktopMrecHalfPage',
                                              pageType: 'article',
                                          },
                                      },
                                  }),
                                  layout.nestedComposition({
                                      type: 'box',
                                      props: {
                                          stickyOffset: 350,
                                          fillContainer: true,
                                      },
                                      contentAreas: {
                                          main: [
                                              layout.component({
                                                  type: 'ad-unit',
                                                  props: {
                                                      noticePosition:
                                                          'below-center',
                                                      padding: [
                                                          0,
                                                          0,
                                                          metrics.thewest
                                                              .margins.md,
                                                          0,
                                                      ],
                                                      slot: {
                                                          id: 'sidebar-middle',
                                                          size: 'desktopMrec',
                                                          pageType: 'article',
                                                      },
                                                  },
                                              }),

                                              layout.component({
                                                  type: 'juliet',
                                                  props: {
                                                      cardType: {
                                                          type: 'landscape',
                                                          format: 'landscape-smallImage',
                                                      },
                                                      removeHorizontalGutters:
                                                          true,
                                                      kickerMode: 'hidden',
                                                      contentIsTangential: true,
                                                      sectionHeader: {
                                                          heading:
                                                              getSidebarHeading(
                                                                  section,
                                                              ),
                                                          section: 'generic',
                                                      },
                                                      dataDefinitionArgs:
                                                          getSidebarDataDefinition(
                                                              section,
                                                          ),
                                                  },
                                              }),
                                          ],
                                      },
                                  }),
                              ],
                          },
                      }),
                  ],
    }
}

const getSidebarHeading = (section: TheWestSection) => {
    if (section === 'default') {
        // the west
        return 'Top Stories'
    }

    // A regional masthead
    return 'Local News'
}

const articleFollowers = (section: TheWestSection) => {
    // Is this the west of a regional masthead?
    const isTheWest = section === 'default'

    const components = []

    if (isTheWest) {
        components.push(
            layout.component({
                feature: 'newsletter-subscription-banner',
                type: 'newsletter-signup',
                props: {
                    text: 'The latest from The West delivered to your inbox daily.',
                    buttonLabel: 'Sign Up',
                },
            }),
        )
    }

    return components
}

const getRippleSectionTargeting = (
    section: TheWestSection,
    article: ArticleV4DTO,
) => {
    if (section !== 'default') {
        return section
    } else {
        return article.topics.primary.parent
            ? article.topics.primary.parent.id
            : article.topics.primary.id
    }
}

const getSidebarDataDefinition = (
    section: TheWestSection,
): ContentDataTypes => {
    if (section !== 'default') {
        const regionalConfig = regionalSiteConfig[section]
        if (regionalConfig) {
            return {
                type: 'listing',
                topics: [regionalConfig.mastheadTopic],
                paging: {
                    page: 1,
                    pageSize: 3,
                },
            }
        }
    }

    return {
        type: 'curation',
        name: 'home',
        offset: 0,
        pageSize: 3,
    }
}

const getBreakingNewsStrap = (section: TheWestSection) => {
    if (section !== 'default') {
        // For now at least we dont want the breaking news strap on any regional masthead
        return []
    }

    return [
        layout.component({
            type: 'breaking-news',
            props: {
                dataDefinitionArgs: {
                    type: 'curation',
                    name: 'breaking-news',
                    offset: 0,
                    pageSize: 1,
                },
            },
        }),
    ]
}
